import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { X, ChevronDown } from "lucide-react";
import Text from "./Text";
import {
  useFetchFrontendBrandsQuery,
  useFetchFrontendCategoriesQuery,
  useFetchFrontendSubCategoriesQuery,
} from "../../../stores/api/pages.api";

const FilterGroup = ({ label, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 py-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-sm font-semibold text-gray-800 mb-2"
      >
        {label}
        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-2 mt-3">{children}</div>}
    </div>
  );
};

const CheckItem = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 accent-gray-900 cursor-pointer"
    />
    <Text is="span" className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
      {label}
    </Text>
  </label>
);

const FilterSidebar = ({ open, onClose, brands: brandsProp, categories: categoriesProp, subCategories: subCategoriesProp }) => {
  const { data: brandsData } = useFetchFrontendBrandsQuery();
  const { data: categoriesData } = useFetchFrontendCategoriesQuery();
  const { data: subCategoriesData } = useFetchFrontendSubCategoriesQuery();

  const brands = brandsProp ?? (brandsData ?? []).map((b) => ({ id: b.id, name: b.name }));
  const categories = categoriesProp ?? (categoriesData ?? []).map((c) => ({ id: c.id, name: c.name }));
  const subCategories = subCategoriesProp ?? (subCategoriesData ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    category: s.category ? { name: s.category.name } : null,
  }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);

  const [selected, setSelected] = useState({
    brand_id: [],
    category_id: [],
    sub_category_id: [],
  });

  // Sync from URL on open
  useEffect(() => {
    if (!open) return;
    setSelected({
      brand_id: searchParams.get("brand_id")?.split(",").filter(Boolean) ?? [],
      category_id: searchParams.get("category_id")?.split(",").filter(Boolean) ?? [],
      sub_category_id: searchParams.get("sub_category_id")?.split(",").filter(Boolean) ?? [],
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = "hidden";
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const toggle = (key, id) => {
    setSelected((prev) => {
      const cur = prev[key];
      return { ...prev, [key]: cur.includes(id) ? cur.filter((v) => v !== id) : [...cur, id] };
    });
  };

  const totalActive = Object.values(selected).flat().length;

  const applyFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ["brand_id", "category_id", "sub_category_id"].forEach((key) => {
        if (selected[key].length) next.set(key, selected[key].join(","));
        else next.delete(key);
      });
      next.delete("page");
      return next;
    });
    onClose();
  };

  const clearAll = () => {
    setSelected({ brand_id: [], category_id: [], sub_category_id: [] });
  };

  const clearAndApply = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ["brand_id", "category_id", "sub_category_id", "page"].forEach((k) => next.delete(k));
      return next;
    });
    onClose();
  };

  // Group sub-categories by their category name
  const subCatsByCategory = useMemo(() => {
    const groups = {};
    subCategories.forEach((s) => {
      const catName = s.category?.name ?? "Other";
      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(s);
    });
    return groups;
  }, [subCategories]);

  if (!open && !visible) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[300] flex transition-opacity duration-300 ${visible && open ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className={`relative z-10 w-[300px] sm:w-[340px] h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ${visible && open ? "translate-x-0" : "-translate-x-full"}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <Text is="h3" className="text-sm font-bold text-gray-900 uppercase tracking-wider">
            Filter{totalActive > 0 && <span className="ml-1 text-xs font-semibold text-white bg-gray-900 rounded-full px-1.5 py-0.5">{totalActive}</span>}
          </Text>
          <button onClick={onClose} aria-label="Close filters" className="text-gray-400 hover:text-gray-900 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Filter groups */}
        <div className="flex-1 overflow-y-auto px-5 custom-scrollbar">

          {/* Brands */}
          {brands.length > 0 && (
            <FilterGroup label="Brand">
              {brands.map((b) => (
                <CheckItem
                  key={b.id}
                  label={b.name}
                  checked={selected.brand_id.includes(String(b.id))}
                  onChange={() => toggle("brand_id", String(b.id))}
                />
              ))}
            </FilterGroup>
          )}

          {/* Categories */}
          {categories.length > 0 && (
            <FilterGroup label="Category">
              {categories.map((c) => (
                <CheckItem
                  key={c.id}
                  label={c.name}
                  checked={selected.category_id.includes(String(c.id))}
                  onChange={() => toggle("category_id", String(c.id))}
                />
              ))}
            </FilterGroup>
          )}

          {/* Sub-categories grouped by category */}
          {subCategories.length > 0 && (
            <FilterGroup label="Sub-Category">
              {Object.entries(subCatsByCategory).map(([catName, subs]) => (
                <div key={catName}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-1.5">{catName}</p>
                  {subs.map((s) => (
                    <CheckItem
                      key={s.id}
                      label={s.name}
                      checked={selected.sub_category_id.includes(String(s.id))}
                      onChange={() => toggle("sub_category_id", String(s.id))}
                    />
                  ))}
                </div>
              ))}
            </FilterGroup>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={clearAndApply}
            className="flex-1 border border-gray-200 text-sm font-semibold text-gray-700 py-2.5 hover:border-gray-900 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={applyFilters}
            className="flex-1 bg-gray-900 text-white text-sm font-semibold py-2.5 hover:bg-black transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilterSidebar;
